import { PropertyType } from '../../shared/models/property-type';
import { Nullable } from '../../shared/types';

// TODO : éclater ces interfaces en plusieurs fichiers
export interface Report {
  id: string;
  historyLegacyId: string;
  evaluationLegacyId: string;
  type: string;
  userExternalId: string;
  organizationExternalId: string;
  createdAt: string;
  propertyType: PropertyType;
  addressStreet: string;
  addressPostalCode: string;
  addressCity: string;
  addressCityCode: string;
  addressInput: string;
  addressAccuracyLevel: null | string;
  districtName: null | string;
  sections: {
    appraisal: Report.Appraisal;
    references: Report.References;
    customer: Report.Customer;
    perks: Report.Perk;
    drawbacks: Report.Drawbacks;
    characteristics: Report.Characteristics;
    expert: Report.Expert;
    organisme: Report.Organisme;
    property: Report.Property;
    photographs: Report.Photographs;
    case: Report.Case;
    tendencies: Nullable<Report.Tendencies>;
    statistics: Nullable<Report.Statistics>;
    cadastre: Report.Cadastre;
    notes: Report.Notes;
    definitions: Report.Definitions;
  };
  geo: Report.GeoReadInfo;
}

export namespace Report {
  export interface GeoReadInfo {
    addressType: string;
    districtId: string;
    districtLabel: string;
    cityId: string;
    cityLabel: string;
    epciId: string;
    epciLabel: string;
    departementId: string;
    departementLabel: string;
  }
  export interface Section {
    isEnabled: boolean;
  }

  export interface Notes extends Section {
    content: string;
  }

  export interface Definitions extends Section {
    content: string;
  }

  export interface Appraisal extends Section {
    basicInfo: Appraisal.BasicInfo;
    appraisedValue: string;
    lowEstimate: string;
    lowEstimatePercentage: number;
    highEstimate: string;
    highEstimatePercentage: number;
    priceM2: number;
    area: number;
    appraisedValueWithAddedReducedValues: number;
    lowEstimateWithAddedReducedValues: number;
    lowEstimatePercentageWithAddedReducedValues: number;
    highEstimateWithAddedReducedValues: number;
    highwayAccessEstimatePercentageWithAddedReducedValues: number;
    priceM2WithAddedReducedValues: number;
    addedReducedValues: {
      category: string;
      value: number;
      percentage: number;
      description: string;
    }[];
  }

  export namespace Appraisal {
    export interface BasicInfo {
      lead: string;
      comment: string;
      chosenValue: string;
      chosenLowValue: string;
      chosenHighValue: string;
      conclusion: string;
    }
  }

  export interface References extends Section {
    isDisplayModeDetailled: boolean;
    isConfidential: boolean;
    isSelection: boolean;
    purchasedCount: number;
    selectedCount: number;
  }

  export interface Customer extends Section {
    firstName: string;
    lastName: string;
    address: string;
    postalCode: string;
    city: string;
    phone: string;
    email: string;
  }

  export interface Perk extends Section {
    items: string[];
  }

  export interface Drawbacks extends Section {
    items: string[];
  }

  export interface Characteristics extends Section {
    constructionYear: number;
    maison?: Characteristics.Maison;
    appartement?: Characteristics.Appartement;
    bienAgricole?: Characteristics.BienAgricole;
    bienViticole?: Characteristics.BienViticole;
    terrain?: Characteristics.Terrain;
    localActivite?: Characteristics.LocalActivite;
    garage?: Characteristics.Garage;
    immeuble?: Characteristics.Immeuble;
  }

  export namespace Characteristics {
    export interface Maison {
      livingAreaM2: number;
      livingRoomAreaM2: number;
      exposure: Nullable<string>;
      numberOfRooms: Nullable<string>;
      numberOfBedrooms: Nullable<string>;
      landAreaM2: number;
      numberOfBathrooms: Nullable<string>;
      ghgEmissions: Nullable<string>;
      energyPerformance: Nullable<string>;
      level: Nullable<string>;
      heating: string;
    }

    export interface Appartement {
      livingAreaM2: number;
      livingRoomAreaM2: number;
      exposure: Nullable<string>;
      numberOfRooms: Nullable<string>;
      numberOfBedrooms: Nullable<string>;
      numberOfBathrooms: Nullable<string>;
      heating: string;
      ghgEmissions: Nullable<string>;
      energyPerformance: Nullable<string>;
      floor: string;
    }

    export interface BienAgricole {
      hasFarmBuildings: boolean;
      hasResidentialBuilding: boolean;
      landAreaHa: number;
      farmBuildingsAreaM2: number;
      residentialBuildingsAreaM2: number;
      cropType: string;
    }

    export interface BienViticole {
      hasFarmBuildings: boolean;
      hasResidentialBuilding: boolean;
      vineyardAreaHa: number;
      farmBuildingsAreaM2: number;
      residentialBuildingsAreaM2: number;
      grapeVarieties: string;
      appellation: string;
    }

    export interface Terrain {
      areaM2: number;
      facadeWidthM2: number;
      viability: Nullable<string>;
      plotRatio: number;
      accessibility: string;
    }

    export interface LocalActivite {
      areaM2: number;
      nature: string;
      numberOfParkingSpaces: number;
      publicTransportNearby: string[];
    }

    export interface Garage {
      garageType: Nullable<string>;
      numberOfParkingSlots: number;
      floor: string;
    }

    export interface Immeuble {
      numberOfUnits: number;
      hasElevator: boolean;
      hasCondominium: boolean;
      profitability: number;
      totalAreaM2: number;
      premisesUsage: string;
    }

    export enum Transport {
      HIGHWAY_ACCESS = 'highway_access',
      AIRPORT = 'airport',
      BUS = 'bus',
      TRAIN_STATION = 'train_station',
      SUBWAY = 'subway',
      PARK_AND_RIDE = 'park_and_ride',
      TRAMWAY = 'tramway',
    }

    export enum GarageType {
      CLOSED_BOX_COLLECTIVE_OR_INDIVIDUAL_GARAGE = 'closed_box_collective_or_individual_garage',
      OPEN_AIR_PARKING_SPOT = 'open_air_parking_spot',
      COVERED_COLLECTIVE_PARKING = 'covered_collective_parking',
      GARAGE_BUILDING_LESS_THAN_500_M2 = 'garage_building_less_than_500_m2',
      INDIVIDUAL_TWO_WHEELER_PARKING = 'individual_two_wheeler_parking',
    }

    export enum Viability {
      NON_BUILDABLE = 'non_buildable',
      BUILDABLE = 'buildable',
      DEVELOPED_WITH_UTILITIES = 'developed_with_utilities',
    }
  }

  export interface Expert extends Section {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    experiences: string;
    role: string;
    roleLabel: string;
    photo?: string;
  }

  export interface Organisme extends Section {
    name: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    postalCode: string;
    city: string;
    expertise: string;
    services: {
      title: string;
      description: string;
    }[];
    photo?: string;
    logo?: string;
  }

  export interface Property extends Section {
    isEnabled: boolean;
    description: string;
    surroundingsDescription: string;
    viewingDate: string;
    occupation: string;
    condition: string;
    comments: string;
  }

  export interface Photographs extends Section {
    isEnabled: boolean;
    photographs: {
      title: string;
      index: number;
      photo?: string;
    }[];
  }

  export interface Case extends Section {
    isEnabled: boolean;
    reference: string;
    date: string;
    address: string;
    connectionToTheProperty: string;
    purpose: string;
    comments: string;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface Statistics extends Section {}

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface Tendencies extends Section {
    isEnabled: boolean;
    marketStudy: boolean;
    marketStudyScope: string,
    inseeGraph: boolean;
  }

  export interface Cadastre extends Section {
    plotPrefixe: string;
    plotSection: string;
    plotNumber: string;
    plotReference: string;
    comment: string;
    plotTotalArea: number;
    plotBuildingArea: number;
    plotFreeArea: Nullable<number>;
    buildings: Cadastre.Building[];
  }

  export namespace Cadastre {
    export interface Building {
      materials: string;
      area: number;
    }
  }
}
